import { Injectable } from '@angular/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { CookiePreferences } from '../../shared/components/organisms/cookie-banner/models/cookie-preferences.model';
import { StorageKeysEnum } from '../../shared/constants';
import { GoogleTagManagerService } from '../google';
import { LoggerService } from '../logs/logger.service';
import { ScriptLoaderService } from '../scripts';
import { StorageService } from '../storage';
import { CookiesConfigScripts } from './constants/cookies-config-scripts.constant';

@Injectable({
  providedIn: 'root',
})
export class CookiesService {
  constructor(
    private readonly ssrCookiesService: SsrCookieService,
    private readonly scriptLoaderService: ScriptLoaderService,
    private readonly gtmService: GoogleTagManagerService,
    private readonly storageService: StorageService,
    private readonly logger: LoggerService,
  ) {}

  set<T>(name: string, value: T | string, expiresInDays = 7, path = '/') {
    if (!this.ssrCookiesService) return;

    const cookieValue = typeof value === 'string' ? value : JSON.stringify(value);

    const expires = expiresInDays > 0 ? expiresInDays : undefined;

    try {
      this.ssrCookiesService.set(name, cookieValue, expires, path, undefined, true);
    } catch (err) {
      this.logger.error(this.constructor.name, err);
    }
  }

  /**
   * Get cookie value as type T.
   * Always returns T when a fallback is provided.
   */
  get<T>(name: string, fallbackValue: T): T;

  /**
   * Get cookie value as plain string.
   * Returns string or null if not found.
   */
  get(name: string, fallbackValue?: string): string | null;

  /**
   * Implementation of cookie getter.
   * Handles both typed and string cases.
   */
  get<T>(name: string, fallbackValue?: T | string): T | string | null {
    if (!this.check(name)) return (fallbackValue ?? null) as T | string | null;

    const cookieValue = this.getRaw(name)!;

    try {
      return JSON.parse(cookieValue) as T;
    } catch (error) {
      return cookieValue;
    }
  }

  check(name: string): boolean {
    return this.ssrCookiesService.check(name);
  }

  private getRaw(name: string): string | null {
    return this.ssrCookiesService.get(name) || null;
  }

  setCookiePreferences(preferences: CookiePreferences): void {
    const finalPreferences: CookiePreferences = preferences.all
      ? {
          ...preferences,
          functional: true,
          technical: true,
          advertisement: true,
          analytics: true,
          necessary: true,
        }
      : preferences;

    this.storageService.setItem<CookiePreferences>(
      StorageKeysEnum.COOKIES_CONFIG,
      finalPreferences,
    );

    if (preferences.technical) this.setTechnicalCookies();

    this.gtmService.setConsent();
  }

  getCookiePreferences(): CookiePreferences | null {
    return this.storageService.getItem<CookiePreferences>(StorageKeysEnum.COOKIES_CONFIG);
  }

  private setTechnicalCookies(): void {
    CookiesConfigScripts.TECHNICAL_COOKIES.forEach((script) => {
      this.scriptLoaderService.addScriptElement(script);
    });
  }
}
